import { nextTick } from 'vue';
import { GlAlert, GlSkeletonLoader, GlModal, GlSafeHtmlDirective } from '@gitlab/ui';
import { translate } from '../../../../../utils/i18n';
import { contextItemValidator } from '../utils';
import { CONTEXT_ITEM_CATEGORY_LOCAL_GIT, LANGUAGE_IDENTIFIER_DIFF, LANGUAGE_IDENTIFIER_PREFIX, LANGUAGE_IDENTIFIER_PLAINTEXT, CONTEXT_ITEM_CATEGORY_DEPENDENCY } from '../constants';
import __vue_normalize__ from 'vue-runtime-helpers/dist/normalize-component.js';

var script = {
  name: 'GlDuoChatContextItemDetailsModal',
  components: {
    GlAlert,
    GlSkeletonLoader,
    GlModal
  },
  directives: {
    SafeHtml: GlSafeHtmlDirective
  },
  inject: {
    renderGFM: {
      from: 'renderGFM',
      default: () => element => {
        element.classList.add('duo-chat-markdown', 'duo-chat-compact-markdown');
      }
    }
  },
  props: {
    /**
     * Context items to preview. If it has no `content`, the loading state will be displayed.
     */
    contextItem: {
      type: Object,
      required: true,
      validator: contextItemValidator
    }
  },
  data() {
    return {
      contentErrorIsVisible: false
    };
  },
  computed: {
    isLoadingContent() {
      return this.contextItem.content === undefined;
    },
    languageIdentifierClass() {
      var _this$contextItem$met, _this$contextItem$met2, _this$contextItem$met3;
      if (this.contextItem.category === CONTEXT_ITEM_CATEGORY_LOCAL_GIT) {
        return LANGUAGE_IDENTIFIER_DIFF;
      }
      const fileExtension = (_this$contextItem$met = this.contextItem.metadata) === null || _this$contextItem$met === void 0 ? void 0 : (_this$contextItem$met2 = _this$contextItem$met.relativePath) === null || _this$contextItem$met2 === void 0 ? void 0 : _this$contextItem$met2.split('.').at(-1);
      if (fileExtension && fileExtension !== ((_this$contextItem$met3 = this.contextItem.metadata) === null || _this$contextItem$met3 === void 0 ? void 0 : _this$contextItem$met3.relativePath)) {
        return `${LANGUAGE_IDENTIFIER_PREFIX}${fileExtension}`;
      }
      return LANGUAGE_IDENTIFIER_PLAINTEXT;
    },
    title() {
      var _this$contextItem$met4, _this$contextItem$met5;
      return ((_this$contextItem$met4 = this.contextItem.metadata) === null || _this$contextItem$met4 === void 0 ? void 0 : _this$contextItem$met4.title) || ((_this$contextItem$met5 = this.contextItem.metadata) === null || _this$contextItem$met5 === void 0 ? void 0 : _this$contextItem$met5.relativePath) || translate('GlDuoChatContextItemDetailsModal.title', 'Preview');
    },
    isDependencies() {
      return this.contextItem.category === CONTEXT_ITEM_CATEGORY_DEPENDENCY;
    }
  },
  watch: {
    contextItem: {
      async handler(newVal, oldVal) {
        // Dependency items contain structured data as content, not code/markdown.
        // So skip running this content through GFM, we'll parse and render it here in the component.
        if (newVal.category === CONTEXT_ITEM_CATEGORY_DEPENDENCY) {
          return;
        }
        const isUnchangedOrEmptyContent = !(newVal !== null && newVal !== void 0 && newVal.content) || (newVal === null || newVal === void 0 ? void 0 : newVal.content) === (oldVal === null || oldVal === void 0 ? void 0 : oldVal.content);
        if (isUnchangedOrEmptyContent) {
          return;
        }
        await nextTick();
        await this.hydrateContentWithGFM();
      },
      immediate: true
    }
  },
  methods: {
    async hydrateContentWithGFM() {
      await nextTick();
      if (this.$refs.content) {
        this.renderGFM(this.$refs.content);
      }
    },
    parseDependencies() {
      if (this.contextItem.category !== CONTEXT_ITEM_CATEGORY_DEPENDENCY) {
        return null;
      }
      if (!this.contextItem.content) {
        return null;
      }
      try {
        return JSON.parse(this.contextItem.content);
      } catch (error) {
        this.contentErrorIsVisible = true;
        return {};
      }
    },
    onModalVisibilityChange(isVisible) {
      if (!isVisible) {
        this.$emit('close');
      }
    }
  },
  CONTENT_ERROR_MESSAGE: translate('GlDuoChatContextItemDetailsModal.contentErrorMessage', 'Item content could not be displayed.')
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('gl-modal',{attrs:{"modal-id":"context-item-details-modal","title":_vm.title,"visible":true,"scrollable":true,"hide-footer":"","size":"lg"},on:{"change":_vm.onModalVisibilityChange}},[(_vm.isLoadingContent)?_c('gl-skeleton-loader'):(_vm.contentErrorIsVisible)?_c('gl-alert',{attrs:{"variant":"danger","dismissible":false,"data-testid":"content-error-alert"}},[_vm._v("\n    "+_vm._s(_vm.$options.CONTENT_ERROR_MESSAGE)+"\n  ")]):(_vm.isDependencies)?_c('div',{attrs:{"data-testid":"context-item-content"}},[_c('p',[_vm._v("Project dependencies from "+_vm._s(_vm.contextItem.metadata.secondaryText))]),_vm._v(" "),_vm._l((_vm.parseDependencies()),function(matches,index){return _c('div',{key:index},_vm._l((matches),function(dependencies,language){return _c('div',{key:language},[_c('h3',{staticClass:"gl-heading-4 gl-mb-2"},[_vm._v(_vm._s(language))]),_vm._v(" "),_c('ul',{staticClass:"gl-pl-6"},_vm._l((dependencies),function(dependency){return _c('li',{key:dependency},[_vm._v("\n            "+_vm._s(dependency)+"\n          ")])}),0)])}),0)})],2):_c('div',{ref:"content",attrs:{"data-testid":"context-item-content"}},[_c('pre',{directives:[{name:"safe-html",rawName:"v-safe-html",value:(_vm.contextItem.content),expression:"contextItem.content"}],staticClass:"code js-syntax-highlight gl-p-3",class:_vm.languageIdentifierClass})])],1)};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = __vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export default __vue_component__;
